import React, { useEffect, useState } from 'react';
import { useRecordContext } from 'react-admin';
import { format, addMonths, addWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import uk from 'date-fns/locale/uk';

import axiosApiInstance, { postRequestCacheConfig } from 'src/api/axiosConfig';
import EmployeeReportToolbar from 'src/components/Employees/form/EmployeeReportToolbar';
import { LessonsSection } from 'src/components/Employees/EmployeeShow/EmployeeReportTab/LessonReport';
import { ChildFormFields } from 'src/constants/children/form';
import { FORMAT_DATE_STRING } from 'src/constants';

interface LessonCount {
    groupCount: number;
    individualCount: number;
    totalCount: number;
}

interface ChildReporting {
    generalCount: LessonCount;
    lessonNamesToCount: any;
}

export const ChildReportTab: React.FC = () => {
    const record = useRecordContext();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [startDate, setStartDate] = useState<Date | undefined>(new Date());
    const [endDate, setEndDate] = useState<Date | undefined>(new Date());
    const [childLessons, setChildLessons] = useState<ChildReporting | null>(null);
    const [view, setView] = useState('month');

    // Set the date range when the component mounts and when the view changes
    useEffect(() => {
        setDateRange(currentDate, view);
    }, [currentDate, view]);

    const setDateRange = (currentDate: Date, view: string) => {
        if (view === 'month') {
            setStartDate(startOfMonth(currentDate));
            setEndDate(endOfMonth(currentDate));
        } else if (view === 'week') {
            setStartDate(startOfWeek(currentDate, { locale: uk }));
            setEndDate(endOfWeek(currentDate, { locale: uk }));
        } else {
            // "all" view, don't set start and end dates
            setStartDate(undefined);
            setEndDate(undefined);
        }
    };

    // Effect for data fetching that triggers when startDate/endDate changes
    useEffect(() => {
        if (record && record.id) {
            const educationCenterId = record?.[ChildFormFields.VISIT]?.[ChildFormFields.EDUCATION_CENTER];

            if (!educationCenterId) {
                console.warn('No education center ID found for child');
                return;
            }

            const requestData = {
                userId: record.id,
                educationCenterId: Number(educationCenterId)
            };

            let url = `children/reporting`;

            if (startDate !== undefined && endDate !== undefined) {
                const formattedStartDate = format(new Date(startDate), FORMAT_DATE_STRING);
                const formattedEndDate = format(new Date(endDate), FORMAT_DATE_STRING);

                url += `?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
            }

            axiosApiInstance
                .post(url, requestData, postRequestCacheConfig)
                .then((res) => {
                    if (res.data && res.data.childReporting) {
                        setChildLessons(res.data.childReporting);
                    }
                })
                .catch((e) => {
                    console.error('Error fetching child reports:', e);
                });
        }
    }, [startDate, endDate, record, view]);

    /**
     * Handles view change events
     * @param { string } newView - new view to switch to ('month', 'week', or 'all')
     */
    const handleViewChange = (newView: string) => {
        setView(newView);

        if (newView === 'all') {
            setStartDate(undefined);
            setEndDate(undefined);
        } else {
            setDateRange(currentDate, newView);
        }
    };

    /**
     * Handles navigation events (previous, next, today)
     * @param { string } action - navigation action ('PREVIOUS', 'NEXT', or 'TODAY')
     */
    const handleNavigate = (action: string) => {
        let newDate = currentDate;

        if (view === 'month') {
            if (action === 'PREVIOUS') newDate = addMonths(currentDate, -1);
            if (action === 'NEXT') newDate = addMonths(currentDate, 1);
            if (action === 'TODAY') newDate = new Date();
        } else if (view === 'week') {
            if (action === 'PREVIOUS') newDate = addWeeks(currentDate, -1);
            if (action === 'NEXT') newDate = addWeeks(currentDate, 1);
            if (action === 'TODAY') newDate = new Date();
        }

        setCurrentDate(newDate);
    };

    // Effect for updating the date range when the "Today" button is clicked
    useEffect(() => {
        if (view !== 'all') {
            setDateRange(currentDate, view);
        }
    }, [currentDate, view]);

    /**
     * Gets the label for the current date range
     */
    const getLabel = () => {
        if (view === 'month') {
            const monthLabel = format(currentDate, 'LLLL yyyy', { locale: uk });
            return monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
        } else if (view === 'week') {
            return `${startDate && format(startDate, 'd MMMM', { locale: uk })} - ${endDate && format(endDate, 'd MMMM yyyy', { locale: uk })}`;
        } else if (view === 'all') {
            return 'За весь період';
        } else {
            return '';
        }
    };

    return (
        <>
            <EmployeeReportToolbar
                date={currentDate}
                view={view}
                label={getLabel()}
                handleViewChange={handleViewChange}
                handleNavigate={handleNavigate}
            />
            {childLessons && (
                <LessonsSection
                    title="Отримано освітніх послуг"
                    reportType={'CHILD_REPORT'}
                    generalCount={childLessons.generalCount}
                    lessonNamesToCount={childLessons.lessonNamesToCount}
                    startDate={startDate}
                    endDate={endDate}
                    childId={(record && record?.id.toString()) || ''}
                />
            )}
        </>
    );
};

export default ChildReportTab;
